import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProductById } from 'api/repository/products.api';
import ProductDetailsCard from 'features/products/components/ProductDetailsCard';
import { useTranslation } from 'context/LanguageContext';
import Loading from 'shared/components/Loading';
import BackButton from 'shared/components/BackButton';

const normalizeProduct = (product = {}) => ({
  ...product,
  quantity: product.quantity ?? product.qty ?? 0,
  selling_price: product.selling_price ?? product.price ?? 0,
  cost_price: product.cost_price ?? product.price ?? 0,
  low_stock_alert: product.low_stock_alert ?? 0,
  discount: product.discount ?? 0,
  is_offer: product.is_offer ?? false,
  isTracked: product.isTracked ?? false,
  sizes: Array.isArray(product.sizes) ? product.sizes : [],
  colors: Array.isArray(product.colors) ? product.colors : [],
});

const parseProductResponse = (rawResponse) => {
  const payload =
    rawResponse && rawResponse.code !== undefined
      ? rawResponse
      : rawResponse?.data ?? rawResponse;

  if (!payload) {
    return { product: null, relatedProducts: [], error: 'Empty response' };
  }

    if (payload.code !== 200) {
      return {
        product: null,
        relatedProducts: [],
        error: payload.message || 'Product not found',
      };
    }

    const dataNode = payload.data || {};
    const productNode = dataNode.product || dataNode;
    const relatedNode = dataNode.relatedProducts || dataNode.related_products || [];

    return {
      product: productNode ? normalizeProduct(productNode) : null,
      relatedProducts: Array.isArray(relatedNode)
        ? relatedNode.map((item) => normalizeProduct(item))
        : [],
    };
  

 
};

const ProductDetailsPage = () => {
  const { productId } = useParams();
  const { language } = useTranslation();
  const isRTL = language === 'ar';
  
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductDetails = async () => {
      const normalizedProductId = String(productId || "").trim();
      if (!normalizedProductId || normalizedProductId === "undefined" || normalizedProductId === "null") {
        setError('Invalid product id');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await getProductById(normalizedProductId);
        const parsed = parseProductResponse(response);

        if (parsed.error) {
          setError(parsed.error);
          return;
        }

        if (!parsed.product) {
          setError('Unexpected response format');
          return;
        }

        setProduct(parsed.product);
        setRelatedProducts(parsed.relatedProducts);
      } catch (err) {
        const requestUrl = err?.config
          ? `${err.config.baseURL || ""}${err.config.url || ""}`
          : "";
        console.error('Error fetching product:', {
          productId: normalizedProductId,
          status: err?.response?.status,
          requestUrl,
          error: err,
        });
        
        if (err.code === 'ECONNABORTED') {
          setError('Request timeout. Please try again.');
        } else if (err.message === 'Network Error') {
          setError('Cannot connect to server. Please check if the backend is running.');
        } else if (err.response?.status === 404) {
          setError('Product not found.');
        } else {
          setError(err.response?.data?.message || err.message || 'Failed to load product');
        }
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProductDetails();
    }
  }, [productId]);

  if (loading) {
    return (
      <Loading/>
    );
  }

  if (error || !product) {
    return (
      <>
        <div className="bg-white shadow-sm sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <BackButton title="Product Details" />
          </div>
        </div>
        
        <div 
          className="min-h-screen flex items-center justify-center bg-gray-50" 
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
            <svg 
              className="w-16 h-16 text-red-500 mx-auto mb-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <p className="text-red-600 mb-2 text-lg font-medium">
              {error || 'Product not found'}
            </p>
            <p className="text-gray-500 text-sm">Product ID: {productId}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <BackButton title={product.name} />
        </div>
      </div>
      
      <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container mx-auto px-4 pt-0">
          <ProductDetailsCard
            product={product}
            relatedProducts={relatedProducts}
          />
        </div>
      </div>
    </>
  );
};

export default ProductDetailsPage;
